import { Link, useNavigate } from '@tanstack/react-router';
import { Mail } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { TwoFactorChallenge } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { PasswordInput } from '@/shared/forms/password-input';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { toGoogleStart, toStaffLogin } from '../api/mappers';
import { useLogin } from '../hooks/use-login';
import { useSignInProviders } from '../hooks/use-sign-in-providers';
import { loginSchema, type LoginValues } from '../schemas/login-schema';
import { isTwoFactorChallenge, type ChallengeEnd } from '../schemas/two-factor-rules';

import { GoogleSignInButton } from './google-sign-in-button';
import { ServerError } from './server-error';
import { TwoFactorStep } from './two-factor-step';

export interface SignInCardProps {
  /** Where to go after sign-in; the organisation home by default. */
  redirectTo?: string | undefined;
  /** `?organization=`: which organisation to open for someone who belongs to several. */
  organizationSlug?: string | undefined;
}

/**
 * Email + password or Google, then the two-factor code step when the backend answers 202. The
 * cookie set by login or verify is the session. The optional organisation applies to both.
 */
export function SignInCard({ redirectTo, organizationSlug }: SignInCardProps) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const login = useLogin();
  const providers = useSignInProviders();
  // Hidden while loading so they never flash; a failed lookup keeps both, the backend still
  // refuses what is switched off (Google's 409 explains itself).
  const offerGoogle = providers.data?.google ?? providers.isError;
  const offerMagicLink = providers.data?.magicLink ?? providers.isError;
  const form = useForm<LoginValues>({
    resolver: useI18nResolver(loginSchema),
    defaultValues: { email: '', password: '', organizationSlug: organizationSlug ?? '' },
  });
  const [pickOrganization, setPickOrganization] = useState(Boolean(organizationSlug));
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [challengeEnd, setChallengeEnd] = useState<ChallengeEnd | null>(null);
  const { resetField } = form;
  const { reset: resetLogin } = login;

  const enter = useCallback(
    () => navigate({ to: redirectTo ?? '/org', replace: true }),
    [navigate, redirectTo],
  );
  const backToPassword = useCallback(
    (end: ChallengeEnd | null) => {
      setChallenge(null);
      setChallengeEnd(end);
      resetField('password');
      resetLogin();
    },
    [resetField, resetLogin],
  );
  const onBack = useCallback(() => backToPassword(null), [backToPassword]);

  const googleRequest = async () => {
    if (!(await form.trigger('organizationSlug'))) return null;
    return toGoogleStart(form.getValues('email'), form.getValues('organizationSlug'));
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setChallengeEnd(null);
    try {
      const result = await login.mutateAsync(toStaffLogin(values));
      if (isTwoFactorChallenge(result)) {
        setChallenge(result);
        return;
      }
      await enter();
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  if (challenge) {
    return (
      <div className="bg-card text-card-foreground w-full max-w-[400px] rounded-2xl border p-7 shadow-[0_1px_2px_rgba(24,24,27,0.04),0_12px_32px_rgba(24,24,27,0.06)] sm:p-9">
        <div className="mb-5 flex flex-col gap-1.5">
          <h2 className="text-[22px] font-semibold tracking-tight">{t('twoFactor.title')}</h2>
        </div>
        <TwoFactorStep
          challenge={challenge}
          onVerified={enter}
          onBack={onBack}
          onExpired={backToPassword}
          backLabel={t('twoFactor.backToPassword')}
        />
      </div>
    );
  }

  return (
    <div className="bg-card text-card-foreground w-full max-w-[400px] rounded-2xl border p-7 shadow-[0_1px_2px_rgba(24,24,27,0.04),0_12px_32px_rgba(24,24,27,0.06)] sm:p-9">
      <div className="mb-5 flex flex-col gap-1.5">
        <h2 className="text-[22px] font-semibold tracking-tight">{t('login.title')}</h2>
        <p className="text-muted-foreground text-sm">{t('login.description')}</p>
      </div>
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          {challengeEnd ? (
            <Alert role="alert">
              <AlertDescription>{t(`twoFactor.${challengeEnd}`)}</AlertDescription>
            </Alert>
          ) : null}
          {login.isError && !login.error.hasFieldErrors ? (
            <ServerError error={login.error} />
          ) : null}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.email')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder={t('fields.emailPlaceholder')}
                    className="h-11"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between gap-2">
                  <FormLabel>{t('fields.password')}</FormLabel>
                  <Link
                    to="/forgot-password"
                    className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline"
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </div>
                <FormControl>
                  <PasswordInput autoComplete="current-password" className="h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {pickOrganization ? (
            <FormField
              control={form.control}
              name="organizationSlug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.organizationSlug')}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="organization"
                      autoCapitalize="off"
                      spellCheck={false}
                      placeholder={t('fields.organizationSlugPlaceholder')}
                      className="h-11"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('login.organizationHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
          <Button type="submit" size="lg" className="h-11 w-full" disabled={login.isPending}>
            {login.isPending ? t('login.submitting') : t('login.submit')}
          </Button>
          {offerGoogle || offerMagicLink ? (
            <div className="text-muted-foreground flex items-center gap-3 text-xs" aria-hidden>
              <span className="bg-border h-px flex-1" />
              {t('login.or')}
              <span className="bg-border h-px flex-1" />
            </div>
          ) : null}
          {offerGoogle ? <GoogleSignInButton getRequest={googleRequest} /> : null}
          {offerMagicLink ? (
            <Button asChild variant="outline" size="lg" className="h-11 w-full">
              <Link to="/sign-in-link">
                <Mail aria-hidden />
                {t('login.emailLink')}
              </Link>
            </Button>
          ) : null}
          {pickOrganization ? null : (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="text-muted-foreground self-center px-0 text-xs"
              onClick={() => setPickOrganization(true)}
            >
              {t('magicLink.severalOrganizations')}
            </Button>
          )}
          <p className="text-muted-foreground text-center text-xs leading-relaxed">
            {t('login.provisioned')}
          </p>
        </form>
      </Form>
    </div>
  );
}
